import * as React from "react";
import { useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { ArrowDown, ArrowUp, Search } from "lucide-react";
import { api } from "@/lib/api";
import type { CreatorListItem } from "@/lib/types";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { CreatorRow } from "@/components/CreatorRow";
import { Skeleton } from "@/components/ui/skeleton";
import { Empty } from "@/components/ui/empty";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

type SortKey =
  | "handle"
  | "members_active_7d"
  | "sends_7d"
  | "revenue_7d_cents"
  | "conversion_7d_pct"
  | "last_self_report_at"
  | "health";

const SORT_LABELS: Record<SortKey, string> = {
  handle: "Creator",
  members_active_7d: "Members (7d)",
  sends_7d: "Sends (7d)",
  revenue_7d_cents: "Revenue (7d)",
  conversion_7d_pct: "Conv %",
  last_self_report_at: "Last report",
  health: "Health",
};

const HEALTH_RANK = { red: 0, yellow: 1, green: 2 } as const;

export function Creators() {
  const [params, setParams] = useSearchParams();
  const search = params.get("search") ?? "";
  const status = params.get("status") ?? "active";
  const sort = (params.get("sort") ?? "revenue_7d_cents") as SortKey;
  const dir = (params.get("dir") ?? "desc") as "asc" | "desc";

  const q = useQuery({
    queryKey: ["creators", { status }],
    queryFn: () => api.creatorsList({ status, limit: 500 }),
  });

  const filtered = React.useMemo(() => {
    const items = q.data?.items ?? [];
    const s = search.trim().toLowerCase();
    const filtered = s
      ? items.filter(
          (c) =>
            c.handle.toLowerCase().includes(s) ||
            c.display_name.toLowerCase().includes(s) ||
            c.creator_id.toLowerCase().includes(s),
        )
      : items;
    return [...filtered].sort((a, b) => cmp(a, b, sort, dir));
  }, [q.data, search, sort, dir]);

  const setSort = (key: SortKey) => {
    setParams((p) => {
      const cur = (p.get("sort") ?? "revenue_7d_cents") as SortKey;
      const curDir = (p.get("dir") ?? "desc") as "asc" | "desc";
      p.set("sort", key);
      p.set("dir", cur === key ? (curDir === "asc" ? "desc" : "asc") : "desc");
      return p;
    });
  };

  return (
    <div className="space-y-4">
      <header className="flex items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">Creators</h1>
          <p className="text-xs text-muted-foreground">
            {q.data ? `${filtered.length} of ${q.data.items.length}` : "loading…"}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select
            value={status}
            onValueChange={(v) => {
              const np = new URLSearchParams(params);
              np.set("status", v);
              setParams(np);
            }}
          >
            <SelectTrigger className="h-8 w-[110px] text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="active">active</SelectItem>
              <SelectItem value="paused">paused</SelectItem>
              <SelectItem value="all">all</SelectItem>
            </SelectContent>
          </Select>
          <div className="relative">
            <Search className="absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="search handles…"
              value={search}
              onChange={(e) => {
                const np = new URLSearchParams(params);
                if (e.target.value) np.set("search", e.target.value);
                else np.delete("search");
                setParams(np);
              }}
              className="h-8 w-56 pl-7 text-xs"
            />
          </div>
        </div>
      </header>

      <div className="overflow-hidden rounded-lg border border-border bg-card/40">
        <Table>
          <TableHeader>
            <TableRow>
              <SortableHead
                k="handle"
                cur={sort}
                dir={dir}
                onClick={setSort}
                label="Creator"
              />
              <TableHead>Status</TableHead>
              <SortableHead
                k="members_active_7d"
                cur={sort}
                dir={dir}
                onClick={setSort}
                label="Members 7d"
                align="right"
              />
              <SortableHead
                k="sends_7d"
                cur={sort}
                dir={dir}
                onClick={setSort}
                label="Sends 7d"
                align="right"
              />
              <SortableHead
                k="revenue_7d_cents"
                cur={sort}
                dir={dir}
                onClick={setSort}
                label="Rev 7d"
                align="right"
              />
              <SortableHead
                k="conversion_7d_pct"
                cur={sort}
                dir={dir}
                onClick={setSort}
                label="Conv %"
                align="right"
              />
              <SortableHead
                k="last_self_report_at"
                cur={sort}
                dir={dir}
                onClick={setSort}
                label="Reported"
                align="right"
              />
              <SortableHead k="health" cur={sort} dir={dir} onClick={setSort} label="Health" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {q.isLoading ? (
              Array.from({ length: 8 }).map((_, i) => (
                <TableRow key={i}>
                  {Array.from({ length: 8 }).map((__, j) => (
                    <td key={j} className="px-3 py-3">
                      <Skeleton className="h-4 w-24" />
                    </td>
                  ))}
                </TableRow>
              ))
            ) : filtered.length === 0 ? (
              <TableRow>
                <td colSpan={8} className="px-4 py-12">
                  <Empty title="No creators match." />
                </td>
              </TableRow>
            ) : (
              filtered.map((c) => <CreatorRow key={c.creator_id} creator={c} />)
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function SortableHead({
  k,
  cur,
  dir,
  onClick,
  label,
  align,
}: {
  k: SortKey;
  cur: SortKey;
  dir: "asc" | "desc";
  onClick: (k: SortKey) => void;
  label: string;
  align?: "right" | "left";
}) {
  const isCur = k === cur;
  return (
    <TableHead className={align === "right" ? "text-right" : ""}>
      <button
        onClick={() => onClick(k)}
        className={
          "inline-flex items-center gap-1 transition-colors hover:text-foreground " +
          (isCur ? "text-foreground" : "")
        }
        title={SORT_LABELS[k]}
      >
        {label}
        {isCur ? (
          dir === "asc" ? (
            <ArrowUp className="h-3 w-3" />
          ) : (
            <ArrowDown className="h-3 w-3" />
          )
        ) : null}
      </button>
    </TableHead>
  );
}

function cmp(a: CreatorListItem, b: CreatorListItem, key: SortKey, dir: "asc" | "desc"): number {
  let av: number | string;
  let bv: number | string;
  switch (key) {
    case "handle":
      av = a.handle.toLowerCase();
      bv = b.handle.toLowerCase();
      break;
    case "members_active_7d":
      av = a.members_active_7d;
      bv = b.members_active_7d;
      break;
    case "sends_7d":
      av = a.sends_7d;
      bv = b.sends_7d;
      break;
    case "revenue_7d_cents":
      av = a.revenue_7d_cents;
      bv = b.revenue_7d_cents;
      break;
    case "conversion_7d_pct":
      av = a.conversion_7d_pct;
      bv = b.conversion_7d_pct;
      break;
    case "last_self_report_at":
      av = a.last_self_report_at ? new Date(a.last_self_report_at).getTime() : 0;
      bv = b.last_self_report_at ? new Date(b.last_self_report_at).getTime() : 0;
      break;
    case "health":
      av = HEALTH_RANK[a.health];
      bv = HEALTH_RANK[b.health];
      break;
  }
  const sign = dir === "asc" ? 1 : -1;
  if (av < bv) return -1 * sign;
  if (av > bv) return 1 * sign;
  return 0;
}
